<template>
  <div class="hypothesis-selector">
    <div class="selector-dropdown" @click="showDropdown = !showDropdown" ref="dropdownRef">
      <span v-if="currentHypothesis" class="selector-text">
        {{ currentHypothesis.name }}
      </span>
      <span v-else class="selector-placeholder">
        -- Sélectionner une hypothèse --
      </span>
      <span class="selector-arrow">▼</span>
    </div>

    <div v-if="showDropdown" class="dropdown-menu">
      <div
        v-for="hypothesis in hypotheses"
        :key="hypothesis.id"
        :class="['dropdown-item', { active: currentHypothesis?.id === hypothesis.id }]"
        @click="selectHypothesis(hypothesis.id)"
      >
        <span class="item-name">{{ hypothesis.name }}</span>
        <div class="item-actions" @click.stop>
          <button
            @click.stop="duplicateHypothesis(hypothesis)"
            class="item-action"
            title="Dupliquer"
          >
            <span class="material-symbols-outlined">content_copy</span>
          </button>
          <button
            @click.stop="editHypothesis(hypothesis)"
            class="item-action"
            title="Modifier"
          >
            <span class="material-symbols-outlined">edit</span>
          </button>
          <button
            @click.stop="deleteHypothesis(hypothesis)"
            class="item-action item-action-danger"
            title="Supprimer"
          >
            <span class="material-symbols-outlined">delete</span>
          </button>
        </div>
      </div>
      <div class="dropdown-divider"></div>
      <div class="dropdown-item dropdown-item-create" @click="handleCreate">
        <span>+ Nouvelle hypothèse</span>
      </div>
    </div>

    <ConfirmModal
      :show="showDeleteConfirm"
      title="Supprimer l'hypothèse"
      :message="deleteConfirmMessage"
      confirm-label="Supprimer"
      :danger="true"
      @confirm="confirmDeleteHypothesis"
      @cancel="hypothesisToDelete = null; showDeleteConfirm = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useHypothesesStore } from '@stores/hypotheses.store';
import { useToast } from '../composables/useToast';
import ConfirmModal from './ConfirmModal.vue';
import { hypothesesApi, type Hypothesis } from '@api/hypotheses.api';

const emit = defineEmits<{
  'open-create-modal': [];
  'open-edit-modal': [hypothesis: Hypothesis];
}>();

const router = useRouter();
const hypothesesStore = useHypothesesStore();
const { success, error: showError } = useToast();

const showDropdown = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const hypotheses = computed(() => hypothesesStore.hypotheses);
const currentHypothesis = computed(() => hypothesesStore.currentHypothesis);

const selectHypothesis = async (id: number) => {
  showDropdown.value = false;
  await hypothesesStore.loadHypothesis(id);
  router.push(`/workspace/${id}`);
};

const editHypothesis = (hypothesis: Hypothesis) => {
  showDropdown.value = false;
  emit('open-edit-modal', hypothesis);
};

const hypothesisToDelete = ref<Hypothesis | null>(null);
const showDeleteConfirm = ref(false);

const deleteConfirmMessage = computed(() =>
  hypothesisToDelete.value
    ? `Êtes-vous sûr de vouloir supprimer l'hypothèse « ${hypothesisToDelete.value.name } » ? Cette action est irréversible.`
    : ''
);

const deleteHypothesis = (hypothesis: Hypothesis) => {
  showDropdown.value = false;
  hypothesisToDelete.value = hypothesis;
  showDeleteConfirm.value = true;
};

const confirmDeleteHypothesis = async () => {
  if (!hypothesisToDelete.value) return;
  const hyp = hypothesisToDelete.value;
  hypothesisToDelete.value = null;
  showDeleteConfirm.value = false;
  try {
    await hypothesesStore.delete(hyp.id);
    success('Hypothèse supprimée avec succès');
    if (currentHypothesis.value?.id === hyp.id) {
      router.push('/');
    }
  } catch (err: any) {
    showError(err.message || 'Erreur lors de la suppression');
  }
};

const duplicateHypothesis = async (hypothesis: Hypothesis) => {
  showDropdown.value = false;
  try {
    const created = await hypothesesApi.duplicate(hypothesis.id);
    await hypothesesStore.fetchAll();
    await hypothesesStore.setCurrentHypothesis(created);
    router.push(`/workspace/${created.id}`);
    success('Hypothèse dupliquée');
  } catch (err: any) {
    showError(err.response?.data?.message || err.message || 'Erreur lors de la duplication');
  }
};

const handleCreate = () => {
  showDropdown.value = false;
  emit('open-create-modal');
};

// Fermer le dropdown en cliquant à l'extérieur
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    showDropdown.value = false;
  }
};

watch(showDropdown, (isOpen) => {
  if (isOpen) {
    document.addEventListener('click', handleClickOutside);
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

onMounted(async () => {
  await hypothesesStore.fetchAll();
  if (currentHypothesis.value) {
    // L'hypothèse est déjà chargée depuis le route
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.hypothesis-selector {
  position: relative;
}

.selector-dropdown {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background-color: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  color: #e0e0e0;
  font-size: 0.9rem;
  min-width: 200px;
  cursor: pointer;
  transition: border-color 0.2s;
}

.selector-dropdown:hover {
  border-color: #555;
}

.selector-dropdown:focus-within {
  border-color: #4a9eff;
}

.selector-text {
  flex: 1;
  text-align: left;
}

.selector-placeholder {
  flex: 1;
  text-align: left;
  color: #666;
}

.selector-arrow {
  margin-left: 0.5rem;
  font-size: 0.7rem;
  color: #999;
  transition: transform 0.2s;
}

.selector-dropdown:hover .selector-arrow {
  color: #e0e0e0;
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: #333;
}

.dropdown-item.active {
  background-color: #2a4a6a;
  border-left: 3px solid #4a9eff;
}

.item-name {
  flex: 1;
  color: #e0e0e0;
  font-size: 0.9rem;
}

.item-actions {
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.2s;
}

.dropdown-item:hover .item-actions {
  opacity: 1;
}

.item-action {
  background: none;
  border: none;
  color: #999;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.item-action .material-symbols-outlined {
  font-size: 18px;
}

.item-action:hover {
  background-color: #444;
  color: #e0e0e0;
}

.item-action-danger:hover {
  background-color: #dc3545;
  color: white;
}

.dropdown-divider {
  height: 1px;
  background-color: #444;
  margin: 0.5rem 0;
}

.dropdown-item-create {
  color: #4a9eff;
  font-weight: 500;
}

.dropdown-item-create:hover {
  background-color: #2a4a6a;
  color: #3a8eef;
}
</style>
